import { ColumnDef, SortingState } from '@tanstack/react-table'
import { useMemo, useState, type JSX } from 'react';
import { DrilldownButton, DrilldownScaffold } from '~/components/button'
import { UserCell, makeNumberColumnV2, makePercentColumnV2 } from '~/components/table'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { AnalyticsQueryFilterContext } from '~/modules/analytics/utils'
import { UserRecord } from '~/stores/stores.types'

import { AnalyticsQueryFilter } from '@hap/analytics'
import { DataTable } from '@hap/components'
import { Timespan } from '@hap/utils'

export function AgentLeaderBoard(props: AnalyticsQueryFilterContext): JSX.Element {
  const [sort, setSortOrder] = useState<SortingState>([])
  const { data } = useAnalytics({
    timespan: props.timespan,
    filters: props.filters,
    aggregations: {
      handledBy: {
        field: 'handledBy',
        dataset: 'interactions',
        hydrate: true,
      },
      count: {
        field: 'id',
        op: 'countDistinct',
        dataset: 'interactions',
      },
      avg: {
        field: 'score',
        op: 'avg',
        dataset: 'evaluationCriteriaScores',
      },
    },
    dataset: 'evaluationCriteriaScores',
    orderBy: [{ alias: 'avg', direction: 'desc' }],
  })

  const baseDrilldownFilters = useMemo(() => {
    return [
      ...props.filters.filter((filter) => !['id', 'publishedAt'].includes(filter.field)),
      { field: 'averageEvaluationScore', op: 'isNotNull' },
    ] satisfies AnalyticsQueryFilter[]
  }, [props.filters])

  const userColumns = useMemo(
    () =>
      [
        {
          accessorKey: 'handledBy',
          header: 'Agent',

          cell: (cell) => {
            const value = cell.getValue<UserRecord>()
            return (
              <DrilldownScaffold
                timespan={props.timespan as Timespan.StaticTimespan}
                filters={[
                  ...baseDrilldownFilters,
                  { field: 'handledByIds', op: 'in', values: [value.id] },
                ]}
              >
                <UserCell user={value} drilldown />
              </DrilldownScaffold>
            )
          },
        },
        makeNumberColumnV2({ fieldKey: 'count', header: 'Interactions' }),
        makePercentColumnV2({ fieldKey: 'avg', header: 'Avg Score' }),
        {
          accessorKey: 'handledBy',
          header: '',
          id: 'drilldown',
          size: 10,
          cell: (cell) => {
            const value = cell.getValue<UserRecord>()
            return (
              <DrilldownButton
                timespan={props.timespan as Timespan.StaticTimespan}
                filters={[
                  ...baseDrilldownFilters,
                  { field: 'handledByIds', op: 'in', values: [value.id] },
                ]}
              />
            )
          },
        },
      ] satisfies ColumnDef<any>[],
    [props.filters],
  )

  return (
    <DataTable
      sorting={sort}
      onSortChange={setSortOrder}
      data={data || []}
      columns={userColumns}
      className="border-0"
    />
  )
}

export function ScorecardLeaderBoard(props: Props): JSX.Element {
  const { data } = useAnalytics({
    timespan: props.timespan,
    filters: props.filters,
    aggregations: {
      scorecardId: {
        field: 'scorecardId',
        dataset: 'evaluationCriteriaScores',
        hydrate: true,
      },
      count: {
        field: 'id',
        op: 'countDistinct',
        dataset: 'interactions',
      },
      avg: {
        field: 'score',
        op: 'avg',
        dataset: 'evaluationCriteriaScores',
      },
      upper: {
        field: 'score',
        op: 'p25',
        dataset: 'evaluationCriteriaScores',
      },
      lower: {
        field: 'score',
        op: 'p75',
        dataset: 'evaluationCriteriaScores',
      },
    },
    dataset: 'evaluationCriteriaScores',
    orderBy: [{ alias: 'avg', direction: 'desc' }],
  })

  const baseDrilldownFilters = useMemo(() => {
    return [
      ...props.filters.filter(
        (filter) => !['scorecardId', 'publishedAt'].includes(filter.field),
      ),
      { field: 'averageEvaluationScore', op: 'isNotNull' },
    ] satisfies AnalyticsQueryFilter[]
  }, [props.filters])

  const scorecardColumns = useMemo(
    () =>
      [
        {
          accessorKey: 'scorecard',
          header: 'Scorecard',
          cell: (cell) => {
            const value = cell.getValue<{ name: string } | null>()
            return value?.name || 'Unknown'
          },
        },
        makeNumberColumnV2({ fieldKey: 'count', header: 'Interactions' }),
        makePercentColumnV2({ fieldKey: 'avg', header: 'Avg Score' }),
        {
          accessorKey: 'scorecard',
          header: '',
          id: 'drilldown',
          size: 10,
          cell: (cell) => {
            const value = cell.getValue<{ id: string }>()
            return (
              <DrilldownButton
                timespan={props.timespan}
                filters={[
                  ...baseDrilldownFilters,
                  {
                    field: 'scorecardId',
                    op: 'in',
                    values: [value.id],
                    dataset: 'evaluations',
                  },
                ]}
              />
            )
          },
        },
      ] satisfies ColumnDef<any>[],
    [props.filters],
  )

  return <DataTable data={data || []} columns={scorecardColumns} className="border-0" />
}

interface Props {
  filters: AnalyticsQueryFilter[]
  orderDirection: 'asc' | 'desc'
  timespan: Timespan.StaticTimespan
}
